import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import useAlertFromLocation from "../../../../hooks/useAlertFromLocation";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";

const List = () => {
  const [receiveData, setReceiveData] = useState([]);
  const [inputs, setInputs] = useState({ status: "", is_closed: "" });
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(false);

  const alert = useAlertFromLocation();

  const fetchData = async (params = {}) => {
    setLoading(true);
    try {
      const [res1, res2] = await Promise.all([Http.get("/demand", { params }), Http.get("/staff-type-check")]);
      setReceiveData(res1.data);
      setRole(res2.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const formSubmit = () => {
    fetchData(inputs);
  };

  // Confirm, then POST a state change (close / approve / lock) for one demand.
  const postAction = (id, url, question) => {
    Swal.fire({
      title: "Are you sure?",
      text: question,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.post(url + "/" + id)
          .then((res) => {
            Swal.fire("Done!", res.data.message, "success");
            fetchData(inputs);
          })
          .catch((e) => {
            Swal.fire("Failed!", e.response?.data?.message || "Something went wrong.", "error");
          });
      }
    });
  };

  const deleteDemand = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This demand will be deleted. You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.delete("/demand/" + id)
          .then((res) => {
            Swal.fire("Deleted!", res.data.message, "success");
            fetchData(inputs);
          })
          .catch((e) => {
            Swal.fire("Failed!", e.response?.data?.message || "Something went wrong.", "error");
          });
      }
    });
  };

  const money = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const cols = [
    { field: "receipt_no", header: "Demand No" },
    {
      field: (item) => (item.distributor ? `${item.distributor.name} (${item.distributor.code})` : ""),
      header: "Distributor",
    },
    { field: (item) => (item.base ? item.base.name : ""), header: "Base" },
    { field: (item) => (item.depot ? item.depot.name : ""), header: "Depot" },
    { field: "order_date", header: "Demand Date" },
    { field: (item) => money(item.total_price), header: "Demand Amount" },
    { field: (item) => money(item.remaining_amount), header: "Remaining" },
    { field: "payment_date", header: "Payment Date" },
    { field: (item) => money(item.payment_amount), header: "Payment" },
    { field: "payment_type", header: "Type" },
    {
      field: (item) =>
        item.status === "draft" ? (
          <span className="text-danger font-weight-bold">Draft</span>
        ) : item.is_closed ? (
          <span className="text-danger">Closed</span>
        ) : (
          <span className="text-success">Active</span>
        ),
      header: "Status",
    },
  ];

  const actionTemplate = (item) => {
    return (
      <td className="text-right" style={{ whiteSpace: "nowrap" }}>
        <Link to={"/demand-sheet-receipt/" + item.id} className="btn btn-info btn-sm" title="Receipt">
          <i className="fa fa-list"></i>
        </Link>
        <Link to={"/demand-wise-order/" + item.id} className="btn btn-secondary btn-sm ms-1" title="Orders">
          <i className="fa fa-truck"></i>
        </Link>
        <Link to={"/demand-remarks/" + item.id} className="btn btn-warning btn-sm ms-1" title="Remarks">
          <i className="fa fa-comment"></i>
        </Link>

        {!item.is_closed && (
          <>
            {item.status === "active" && role === "depot" && (
              <Link
                to={"/demand-generate-order/" + item.id}
                className="btn btn-success btn-sm ms-1"
                title="Generate Order"
              >
                <i className="fa fa-plus"></i>
              </Link>
            )}
            {item.status === "draft" && role === "admin" && (
              <button
                className="btn btn-success btn-sm ms-1"
                title="Approve"
                onClick={() => postAction(item.id, "/demand-approve", "Approve this demand?")}
              >
                <i className="fa fa-check"></i>
              </button>
            )}
            {!item.lock_demand && (
              <Link to={"/edit-demand-sheet/" + item.id} className="btn btn-primary btn-sm ms-1" title="Edit">
                <i className="fa fa-pencil"></i>
              </Link>
            )}
            <button
              className="btn btn-warning btn-sm ms-1"
              title="Close Demand"
              onClick={() => postAction(item.id, "/demand-close", "Close this demand? This cannot be undone.")}
            >
              <i className="fa fa-scissors"></i>
            </button>
            {role === "admin" && (
              <>
                <button
                  className={`btn btn-sm ms-1 ${item.lock_demand ? "btn-warning" : "btn-info"}`}
                  title={item.lock_demand ? "Unlock" : "Lock"}
                  onClick={() =>
                    postAction(item.id, "/demand-lock-toggle", item.lock_demand ? "Unlock this demand?" : "Lock this demand?")
                  }
                >
                  <i className={`fa ${item.lock_demand ? "fa-lock" : "fa-unlock"}`}></i>
                </button>
                <button className="btn btn-danger btn-sm ms-1" title="Delete" onClick={() => deleteDemand(item.id)}>
                  <i className="fa fa-trash"></i>
                </button>
              </>
            )}
          </>
        )}
      </td>
    );
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">All Demand Sheets</h5>
            <Link to="/create-demand-sheet" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i> Demand Sheet
            </Link>
          </div>

          <div className="am-pagebody">
            <Alert alert={alert} />

            <div className="card pd-sm-40">
              <div className="row">
                <div className="col-lg">
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={inputs.start_date || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-lg">
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={inputs.end_date || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-lg">
                  <select className="form-control" name="is_closed" value={inputs.is_closed} onChange={handleChange}>
                    <option value="">All</option>
                    <option value="0">Open</option>
                    <option value="1">Closed</option>
                  </select>
                </div>
                <div className="col-lg">
                  <select className="form-control" name="status" value={inputs.status} onChange={handleChange}>
                    <option value="">Any Status</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
                <div className="col-lg">
                  <button className="btn btn-primary btn-block mg-b-10" onClick={formSubmit}>
                    <i className="fa fa-send mg-r-10"></i> Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="am-pagebody" style={{ paddingTop: "0px" }}>
            <div className="card pd-20 pd-sm-40">
              <ReactDataTable cols={cols} receiveData={receiveData} isLoading={loading} actionTemplate={actionTemplate} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
